import { FC } from 'react';
import { Col, Row, Typography } from 'antd';
import { TbArrowRight, TbFileDescription, TbHistory } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { EmptySection, MiddleDot, SmallTag } from '@/shared';
import { useResourcePreviewChanges } from '@/shared/components/resource-route-preview/changeContext';
import ResourceChangeValue from '@/shared/components/resource-route-preview/ResourceChangeValue';
import ResourcePreviewField from '@/shared/components/resource-route-preview/ResourcePreviewField';
import { ResourceVersionChange, ResourceVersionListItem, VersionedResourceType } from '@/types';
import { dayjs } from '@/utilities';

import { formatHistoryFieldLabel, formatHistoryValue, getResourceHistoryLabel } from '../../helpers';
import { renderResourceSnapshotPreview } from './registry';

interface SnapshotPreviewProps {
    resourceType: VersionedResourceType;
    version?: ResourceVersionListItem;
}

interface ChangeValueProps {
    tone?: 'added' | 'neutral' | 'removed';
    value: unknown;
}

const ChangeValue: FC<ChangeValueProps> = ({ tone = 'neutral', value }): JSX.Element => (
    <ResourceChangeValue tone={tone} value={formatHistoryValue(value)} />
);

interface GenericChangeProps {
    change: ResourceVersionChange;
    isLast: boolean;
}

const GenericChange: FC<GenericChangeProps> = ({ change, isLast }): JSX.Element => {
    const { token } = useAntDesignTheme();

    return (
        <Row
            align='middle'
            wrap={false}
            style={{
                minHeight: '3.5rem',
                padding: '0.75rem 1rem',
                borderBottom: isLast ? 0 : `1px solid ${token.colorBorder}`,
            }}
        >
            <Col flex='12rem' style={{ alignSelf: 'flex-start', paddingTop: '0.25rem' }}>
                <small style={{ color: token.colorTextTertiary }}>{formatHistoryFieldLabel(change.path)}</small>
            </Col>
            <Col flex='auto' style={{ minWidth: 0 }}>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '0.5rem',
                        minWidth: 0,
                    }}
                >
                    <ChangeValue tone='removed' value={change.before} />
                    <TbArrowRight
                        aria-label='Changed to'
                        size='1rem'
                        style={{ display: 'block', flex: '0 0 auto', color: token.colorTextTertiary }}
                    />
                    <ChangeValue tone='added' value={change.after} />
                </div>
            </Col>
        </Row>
    );
};

const GenericPreview: FC<{ resourceType: VersionedResourceType; version: ResourceVersionListItem }> = ({
    resourceType,
    version,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const changes = version.changes ?? [];
    const preview = useResourcePreviewChanges();
    const inlineReview = preview?.version.id === version.id && preview.resourceType === resourceType;

    if (changes.length === 0) {
        const snapshotEntries = Object.entries(version.snapshot ?? {}).filter(([, value]) => value !== undefined);

        return (
            <div style={{ maxWidth: '64rem', margin: '0 auto' }}>
                <Typography.Title level={3} style={{ marginTop: 0 }}>
                    {version.title ?? `${getResourceHistoryLabel(resourceType)} snapshot`}
                </Typography.Title>
                <section style={{ border: `1px dashed ${token.colorBorder}`, borderRadius: token.borderRadius }}>
                    {snapshotEntries.map(([field, value]) => (
                        <Row
                            key={field}
                            wrap={false}
                            style={{ padding: '0.75rem 1rem', borderBottom: `1px solid ${token.colorBorderSecondary}` }}
                        >
                            <Col flex='12rem'>
                                <small style={{ color: token.colorTextTertiary }}>
                                    {formatHistoryFieldLabel(field)}
                                </small>
                            </Col>
                            <Col flex='auto' style={{ minWidth: 0 }}>
                                <ChangeValue value={value} />
                            </Col>
                        </Row>
                    ))}
                </section>
            </div>
        );
    }

    return (
        <div style={{ maxWidth: '64rem', margin: '0 auto' }}>
            <Typography.Title level={3} style={{ marginTop: 0 }}>
                {version.title ?? `${getResourceHistoryLabel(resourceType)} changes`}
            </Typography.Title>
            <section
                aria-label={`${getResourceHistoryLabel(resourceType)} field changes`}
                style={{
                    overflow: 'hidden',
                    border: `1px dashed ${token.colorBorder}`,
                    borderRadius: token.borderRadius,
                }}
            >
                {changes.map((change, index) =>
                    inlineReview ? (
                        <Row
                            key={change.path}
                            wrap={false}
                            style={{
                                padding: '0.75rem 1rem',
                                borderBottom:
                                    index === changes.length - 1 ? 0 : `1px solid ${token.colorBorderSecondary}`,
                            }}
                        >
                            <Col flex='12rem'>
                                <small style={{ color: token.colorTextTertiary }}>
                                    {formatHistoryFieldLabel(change.path)}
                                </small>
                            </Col>
                            <Col flex='auto' style={{ minWidth: 0, overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>
                                <ResourcePreviewField
                                    field={change.path}
                                    resourceId={preview.resourceId}
                                    resourceType={resourceType}
                                >
                                    {formatHistoryValue(change.after)}
                                </ResourcePreviewField>
                            </Col>
                        </Row>
                    ) : (
                        <GenericChange
                            key={`${change.path}-${change.type}`}
                            change={change}
                            isLast={index === changes.length - 1}
                        />
                    ),
                )}
            </section>
        </div>
    );
};

const SnapshotPreview: FC<SnapshotPreviewProps> = ({ resourceType, version }): JSX.Element => {
    if (!version) {
        return (
            <EmptySection
                height='100%'
                icon={<TbHistory />}
                label='Select a Version'
                description='Choose a captured event to inspect its snapshot and changes.'
            />
        );
    }

    if (!version.snapshot) {
        return (
            <EmptySection
                height='100%'
                icon={<TbFileDescription />}
                label='Snapshot Unavailable'
                description='This event does not have a restorable snapshot.'
            />
        );
    }

    const createdAt = dayjs(version.createdAt);
    const preview = renderResourceSnapshotPreview(resourceType, version) ?? (
        <GenericPreview resourceType={resourceType} version={version} />
    );

    return (
        <div style={{ minHeight: '100%' }}>
            <div style={{ maxWidth: resourceType === 'memos' ? '68rem' : '64rem', margin: '0 auto 1.25rem' }}>
                <SmallTag
                    message={
                        <>
                            <strong>{createdAt.format('MMM D, YYYY')}</strong>
                            <MiddleDot horizontalMargin='0.375rem' styles={{ color: 'inherit', opacity: 0.5 }} />
                            <span style={{ color: 'inherit', opacity: 0.65 }}>{createdAt.format('h:mm A')}</span>
                        </>
                    }
                    styles={{ display: 'inline-flex', alignItems: 'center', marginLeft: 0, padding: '0.25rem 0.5rem' }}
                />
            </div>
            {preview}
        </div>
    );
};

export default SnapshotPreview;
